if (!customElements.get('sample-variant-picker')) {
  class SampleVariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.search = this.querySelector('[data-sample-search]');
      this.searchWrap = this.querySelector('[data-sample-search-wrap]');
      this.suggestions = this.querySelector('[data-brand-suggestions]');
      this.grid = this.querySelector('[data-sample-grid]');
      this.cards = Array.from(this.querySelectorAll('[data-sample-card]'));
      this.variantInput = this.querySelector('[data-sample-variant-input]');
      this.addButton = this.querySelector('[name="add"]');
      this.action = this.querySelector('[data-sample-action]');

      if (
        !this.search ||
        !this.searchWrap ||
        !this.suggestions ||
        !this.grid ||
        !this.cards.length ||
        !this.variantInput ||
        !this.addButton ||
        !this.action
      ) {
        return;
      }

      this.abortController = new AbortController();
      this.activeBrand = '';
      this.activeSuggestionIndex = -1;
      this.prepareCards();
      this.buildBrandSuggestions();
      this.bindEvents();
      this.filterCards();
      this.observeSelectorVisibility();
      this.observeActionSize();

      if (this.dataset.selectedVariantId) {
        this.selectCard(this.dataset.selectedVariantId, false);
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.visibilityObserver?.disconnect();
      this.actionResizeObserver?.disconnect();
    }

    prepareCards() {
      this.cardData = this.cards.map((card) => {
        const parsed = this.parseVariantLabel(
          card.dataset.variantLabel || '',
          card.dataset.brand || '',
        );
        const brandElement = card.querySelector('[data-card-brand]');
        const nameElement = card.querySelector('[data-card-name]');

        card.dataset.brand = parsed.brand;
        card.dataset.fragrance = parsed.name;
        if (brandElement) {
          brandElement.textContent = parsed.brand;
          brandElement.hidden = !parsed.brand;
        }
        if (nameElement) nameElement.textContent = parsed.name;

        return {
          brand: parsed.brand,
          card,
          name: parsed.name,
          searchText: this.normalise(`${parsed.brand} ${parsed.name}`),
          variantId: card.dataset.variantId,
        };
      });

      this.cardData.sort((left, right) => {
        const brandOrder = left.brand.localeCompare(right.brand, undefined, {
          sensitivity: 'base',
        });
        return (
          brandOrder || left.name.localeCompare(right.name, undefined, { sensitivity: 'base' })
        );
      });
      this.cardData.forEach(({ card }) => this.grid.appendChild(card));
    }

    parseVariantLabel(label, explicitBrand) {
      const cleanLabel = label.trim();
      const cleanBrand = explicitBrand.trim();
      const separators = [' — ', ' – ', ' | ', ' - ', ': '];

      if (cleanBrand) {
        const separator = separators.find((candidate) =>
          cleanLabel.startsWith(`${cleanBrand}${candidate}`),
        );
        return {
          brand: cleanBrand,
          name: separator
            ? cleanLabel.slice(cleanBrand.length + separator.length).trim()
            : cleanLabel,
        };
      }

      const separator = separators.find((candidate) => cleanLabel.includes(candidate));
      if (!separator) return { brand: '', name: cleanLabel };

      const [brand, ...nameParts] = cleanLabel.split(separator);
      return { brand: brand.trim(), name: nameParts.join(separator).trim() };
    }

    buildBrandSuggestions() {
      const brandCounts = new Map();
      this.cardData.forEach(({ brand }) => {
        if (brand) brandCounts.set(brand, (brandCounts.get(brand) || 0) + 1);
      });

      this.brands = Array.from(brandCounts, ([name, count]) => ({ count, name })).sort(
        (left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }),
      );

      this.suggestions.replaceChildren(
        ...this.brands.map(({ count, name }, index) => {
          const option = document.createElement('button');
          option.className = 'sample-picker-suggestion';
          option.id = `${this.dataset.pickerId}-Brand-${index}`;
          option.type = 'button';
          option.tabIndex = -1;
          option.setAttribute('role', 'option');
          option.dataset.brandOption = name;
          option.innerHTML = `<span>${this.escapeHtml(name)}</span><span>${count} ${count === 1 ? 'fragrance' : 'fragrances'}</span>`;
          return option;
        }),
      );
    }

    bindEvents() {
      const { signal } = this.abortController;

      this.search.addEventListener('focus', () => this.openSuggestions(), { signal });
      this.search.addEventListener('input', () => this.filterCards(), { signal });
      this.search.addEventListener('keydown', (event) => this.handleSearchKeydown(event), {
        signal,
      });
      this.searchWrap.addEventListener(
        'focusout',
        () => {
          requestAnimationFrame(() => {
            if (!this.searchWrap.contains(document.activeElement)) this.closeSuggestions();
          });
        },
        { signal },
      );

      this.suggestions.addEventListener(
        'click',
        (event) => {
          const option = event.target.closest('[data-brand-option]');
          if (option) this.selectBrand(option.dataset.brandOption);
        },
        { signal },
      );
      this.grid.addEventListener(
        'click',
        (event) => {
          const card = event.target.closest('[data-sample-card]');
          if (card && !card.disabled) {
            this.selectCard(card.dataset.variantId, true, event.detail === 0);
          }
        },
        { signal },
      );

      this.querySelector('[data-clear-search]').addEventListener(
        'click',
        () => this.clearSearch(),
        {
          signal,
        },
      );
      this.querySelector('[data-clear-brand]').addEventListener('click', () => this.clearBrand(), {
        signal,
      });
      this.querySelector('[data-reset-sample-picker]').addEventListener(
        'click',
        () => this.resetFilters(),
        { signal },
      );
    }

    observeSelectorVisibility() {
      if (!('IntersectionObserver' in window)) return;

      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.dataset.actionVisible = String(entry.isIntersecting);
      });
      this.visibilityObserver.observe(this);
    }

    observeActionSize() {
      if (!('ResizeObserver' in window)) return;

      this.actionResizeObserver = new ResizeObserver(([entry]) => {
        this.style.setProperty(
          '--sample-action-height',
          `${Math.ceil(entry.contentRect.height)}px`,
        );
      });
      this.actionResizeObserver.observe(this.action);
    }

    selectCard(variantId, scrollToAction, focusAddButton = false) {
      if (this.addButton.classList.contains('confirmation')) return;

      const selected = this.cardData.find((item) => item.variantId === String(variantId));
      if (!selected || selected.card.disabled) return;

      this.cardData.forEach(({ card }) =>
        card.setAttribute('aria-pressed', String(card === selected.card)),
      );
      this.variantInput.value = selected.variantId;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.addButton.disabled = false;
      this.addButton.textContent = this.addButton.dataset.addToCartText;
      this.dataset.selectedVariantId = selected.variantId;

      const selectedBrand = this.querySelector('[data-selected-brand]');
      selectedBrand.textContent = selected.brand;
      selectedBrand.hidden = !selected.brand;
      this.querySelector('[data-selected-name]').textContent = selected.name;

      const selectedImage = selected.card.querySelector(
        '.sample-picker-card__image, .sample-picker-card__placeholder',
      );
      const selectedMedia = this.querySelector('[data-selected-media]');
      if (selectedImage && selectedMedia) {
        selectedMedia.replaceChildren(selectedImage.cloneNode(true));
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', selected.variantId);
      window.history.replaceState({}, '', url);

      if (scrollToAction && window.matchMedia('(max-width: 700px)').matches) {
        this.querySelector('[data-sample-action]').scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }
      if (focusAddButton) this.addButton.focus({ preventScroll: true });
    }

    filterCards() {
      const query = this.normalise(this.search.value);
      let visibleCount = 0;

      this.cardData.forEach(({ brand, card, searchText }) => {
        const matchesBrand = !this.activeBrand || brand === this.activeBrand;
        const matchesQuery = !query || searchText.includes(query);
        const visible = matchesBrand && matchesQuery;
        card.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      this.querySelector('[data-result-count]').textContent =
        `${visibleCount} ${visibleCount === 1 ? 'fragrance' : 'fragrances'}`;
      this.querySelector('[data-sample-empty]').hidden = visibleCount !== 0;
      this.querySelector('[data-clear-search]').hidden = !this.search.value;
      if (!this.activeBrand && document.activeElement === this.search) this.openSuggestions();
      this.filterSuggestions();
    }

    openSuggestions() {
      if (this.activeBrand || !this.brands.length) return;
      this.suggestions.hidden = false;
      this.search.setAttribute('aria-expanded', 'true');
      this.filterSuggestions();
    }

    closeSuggestions() {
      this.suggestions.hidden = true;
      this.search.setAttribute('aria-expanded', 'false');
      this.search.removeAttribute('aria-activedescendant');
      this.activeSuggestionIndex = -1;
    }

    filterSuggestions() {
      if (this.suggestions.hidden) return;
      const query = this.normalise(this.search.value);

      this.getSuggestionOptions().forEach((option) => {
        option.hidden =
          Boolean(query) && !this.normalise(option.dataset.brandOption).includes(query);
        option.setAttribute('aria-selected', 'false');
      });
      this.activeSuggestionIndex = -1;
      this.search.removeAttribute('aria-activedescendant');
    }

    selectBrand(brand) {
      this.activeBrand = brand;
      this.search.value = '';
      this.search.placeholder = `Search ${brand}`;
      this.querySelector('[data-active-brand-name]').textContent = brand;
      this.querySelector('[data-active-brand]').hidden = false;
      this.closeSuggestions();
      this.filterCards();
      this.search.focus();
    }

    clearBrand() {
      this.activeBrand = '';
      this.search.value = '';
      this.search.placeholder = 'Search by house or fragrance';
      this.querySelector('[data-active-brand]').hidden = true;
      this.filterCards();
      this.search.focus();
    }

    clearSearch() {
      this.search.value = '';
      this.filterCards();
      this.search.focus();
    }

    resetFilters() {
      this.activeBrand = '';
      this.search.value = '';
      this.search.placeholder = 'Search by house or fragrance';
      this.querySelector('[data-active-brand]').hidden = true;
      this.filterCards();
      this.search.focus();
    }

    handleSearchKeydown(event) {
      if (event.key === 'Escape' && !this.suggestions.hidden) {
        event.preventDefault();
        this.closeSuggestions();
        return;
      }

      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key) || this.suggestions.hidden) return;
      const options = this.getSuggestionOptions().filter((option) => !option.hidden);
      if (!options.length) return;

      if (event.key === 'Enter') {
        const selected =
          options[this.activeSuggestionIndex] ||
          options.find(
            (option) =>
              this.normalise(option.dataset.brandOption) === this.normalise(this.search.value),
          );
        if (selected) {
          event.preventDefault();
          this.selectBrand(selected.dataset.brandOption);
        }
        return;
      }

      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      this.activeSuggestionIndex =
        (this.activeSuggestionIndex + direction + options.length) % options.length;
      options.forEach((option) => option.setAttribute('aria-selected', 'false'));
      const activeOption = options[this.activeSuggestionIndex];
      activeOption.setAttribute('aria-selected', 'true');
      activeOption.scrollIntoView({ block: 'nearest' });
      this.search.setAttribute('aria-activedescendant', activeOption.id);
    }

    getSuggestionOptions() {
      return Array.from(this.suggestions.querySelectorAll('[data-brand-option]'));
    }

    normalise(value) {
      return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase()
        .trim();
    }

    escapeHtml(value) {
      const element = document.createElement('span');
      element.textContent = value;
      return element.innerHTML;
    }
  }

  customElements.define('sample-variant-picker', SampleVariantPicker);
}
